import type { DrivetrainState } from '../drivetrain/VirtualDrivetrain';
import type { AudioConfig } from '../cars/CarProfile';

/**
 * ThrottleProcessor — shapes the mixed engine signal with throttle / rpm:
 * brightness (low-pass), dynamics, lift-off darkening, limiter chop.
 */
export class ThrottleProcessor {
  readonly input: GainNode;
  readonly output: GainNode;
  private readonly lowpass: BiquadFilterNode;
  private readonly dyn: GainNode;
  private chopPhase = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly cfg: AudioConfig,
  ) {
    this.input = ctx.createGain();
    this.lowpass = ctx.createBiquadFilter();
    this.lowpass.type = 'lowpass';
    this.lowpass.Q.value = 0.6;
    this.lowpass.frequency.value = 4000;
    this.dyn = ctx.createGain();
    this.output = ctx.createGain();
    this.input.connect(this.lowpass).connect(this.dyn).connect(this.output);
  }

  /** true for voices whose material already carries the load/level relations (sweep voice) */
  neutralGain = false;
  /**
   * 'clean' voice (AC-style ladders): the filter NEVER moves — a Kunos rung is already voiced for its
   * rpm and load, and the sweep was audibly choking it (closed throttle at idle put the cutoff at
   * ~1.9 kHz over a full-range interior recording). The LEVEL law stays: lift-off ~3.5 dB down and a
   * quieter idle are how the game feels too, and gain moves don't smear a sample the way a filter does.
   */
  cleanVoice = false;

  update(s: DrivetrainState, dtS: number): void {
    const now = this.ctx.currentTime;
    const tau = this.cfg.envelopeTauS;

    // Brightness: opens with throttle and rpm.
    // Recorded loops already carry the right timbre per rpm; keep this subtle.
    // measured on the real cabin: dark at low rpm / light load (centroid ~330 Hz), open under load and revs
    // during a gearchange the pedal is still down: the cut itself is shaped explicitly in AudioEngine
    // (duck + dark lowpass, measured on the real pull) — do not pile a second cut on top
    const thr = s.shifting ? s.throttleIntent : s.throttle;
    // (sweep voice: the passages were recorded at their load — their brightness is the truth, filter stays open)
    let cutoff = this.neutralGain || this.cleanVoice ? 20000 : 1800 + 17000 * Math.pow(clamp01(0.4 * s.rpmNorm + 0.6 * thr), 1.8);
    // lift-off is ~3.5 dB quieter than full load (measured on the real overrun) — the sweep voice already
    // carries that in its ON/OFF recordings, so it runs this stage at unity
    // a real car at idle is far quieter than under way — ~3 dB duck at idle rpm, fully open by ~20 % of the
    // range (Marc: rolling to 2-1-0 km/h the idle sounded as loud as driving, 'queda un poco irreal')
    const idleDuck = 0.7 + 0.3 * clamp01((s.rpmNorm - 0.04) / 0.16);
    let gain = (this.neutralGain ? 1 : 0.66 + 0.34 * thr + 0.08 * s.rpmNorm) * idleDuck;

    if (s.liftOff && !this.neutralGain) {
      if (!this.cleanVoice) cutoff = Math.min(cutoff, this.cfg.liftOffLowpassHz + 6000 * s.rpmNorm);
      gain *= 0.85;
    }

    if (s.limiter) {
      this.chopPhase += dtS * 14;
      const chop = (this.chopPhase % 1) < 0.5 ? 1 : 0.35;
      gain *= chop;
      this.dyn.gain.setTargetAtTime(gain, now, 0.004);
    } else {
      this.dyn.gain.setTargetAtTime(gain, now, tau);
    }
    this.lowpass.frequency.setTargetAtTime(cutoff, now, tau * 1.5);
  }
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
